import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';

import { FilterSelect } from './FilterSelect';

interface IncidentFiltersProps {
  statusFilter: string;
  severityFilter: string;
  sortBy: string;
  onStatusChange: (value: string) => void;
  onSeverityChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onClearFilters: () => void;
  isMobile?: boolean;
}

const sortOptions = [
  { value: 'newest', labelId: 'incidents.filters.sort.newest' as const },
  { value: 'oldest', labelId: 'incidents.filters.sort.oldest' as const },
  {
    value: 'incidentNumber',
    labelId: 'incidents.filters.sort.incidentNumber' as const,
  },
  { value: 'severity', labelId: 'incidents.filters.sort.severity' as const },
  { value: 'status', labelId: 'incidents.filters.sort.status' as const },
];

const statusOptions = [
  { value: 'all', labelId: 'incidents.filters.status.all' as const },
  {
    value: 'triggered',
    labelId: 'incidents.filters.status.triggered' as const,
  },
  {
    value: 'acknowledged',
    labelId: 'incidents.filters.status.acknowledged' as const,
  },
  { value: 'resolved', labelId: 'incidents.filters.status.resolved' as const },
];

const severityOptions = [
  { value: 'all', labelId: 'incidents.filters.severity.all' as const },
  { value: 'SEV-1', labelId: 'incidents.filters.severity.SEV-1' as const },
  { value: 'SEV-2', labelId: 'incidents.filters.severity.SEV-2' as const },
  { value: 'SEV-3', labelId: 'incidents.filters.severity.SEV-3' as const },
  { value: 'SEV-4', labelId: 'incidents.filters.severity.SEV-4' as const },
  { value: 'SEV-5', labelId: 'incidents.filters.severity.SEV-5' as const },
];

export const IncidentFilters = ({
  statusFilter,
  severityFilter,
  sortBy,
  onStatusChange,
  onSeverityChange,
  onSortChange,
  onClearFilters,
  isMobile = false,
}: IncidentFiltersProps): JSX.Element => {
  const hasActiveFilters = statusFilter !== 'all' || severityFilter !== 'all';

  if (isMobile) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              <FormattedMessage id="incidents.filters.title" />
            </h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="h-8 px-2 text-xs"
              >
                <FormattedMessage id="incidents.filters.clear" />
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <FilterSelect
              id="sort-select-mobile"
              label="incidents.filters.sortLabel"
              value={sortBy}
              onValueChange={onSortChange}
              options={sortOptions}
            />
            <FilterSelect
              id="status-select-mobile"
              label="incidents.filters.statusLabel"
              value={statusFilter}
              onValueChange={onStatusChange}
              options={statusOptions}
            />
            <FilterSelect
              id="severity-select-mobile"
              label="incidents.filters.severityLabel"
              value={severityFilter}
              onValueChange={onSeverityChange}
              options={severityOptions}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 p-4 border-r border-gray-200 dark:border-gray-700">
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            <FormattedMessage id="incidents.filters.title" />
          </h3>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="mb-4 h-8 px-2 text-xs"
            >
              <FormattedMessage id="incidents.filters.clear" />
            </Button>
          )}
        </div>

        <FilterSelect
          id="sort-select"
          label="incidents.filters.sortLabel"
          value={sortBy}
          onValueChange={onSortChange}
          options={sortOptions}
        />

        <FilterSelect
          id="status-select"
          label="incidents.filters.statusLabel"
          value={statusFilter}
          onValueChange={onStatusChange}
          options={statusOptions}
        />

        <FilterSelect
          id="severity-select"
          label="incidents.filters.severityLabel"
          value={severityFilter}
          onValueChange={onSeverityChange}
          options={severityOptions}
        />
      </div>
    </div>
  );
};
